import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { Target, Eye, Heart, Linkedin } from 'lucide-react';

const teamMembers = [
  {
    name: 'Stéphane Revil',
    role: 'Managing Partner',
    image: 'https://cdn.prod.website-files.com/65bab6e8de90f51ce337cb55/65bac163c94e137b1321e69b_Design%20sans%20titre%20(6).png',
    bio: 'Fort d\'une expérience solide dans le secteur culturel et la gestion de projets, Stéphane pilote la stratégie globale du fonds et supervise les partenariats avec les mécènes.',
    linkedin: 'https://www.linkedin.com/in/st%C3%A9phane-revil-74926b21/',
  },
  {
    name: 'Mattéo Nathan',
    role: 'Co-Founder & Acquisitions',
    image: 'https://cdn.prod.website-files.com/65bab6e8de90f51ce337cb55/65bac23357b703331ea03815_Design%20sans%20titre%20(7).png',
    bio: 'Passionné de cinéma engagé, Mattéo est responsable de la sélection des œuvres et de l\'acquisition des droits de diffusion auprès des producteurs.',
    linkedin: 'https://www.linkedin.com/in/matt%C3%A9o-nathan-759454157/',
  },
  {
    name: 'Terence Dossou',
    role: 'Co-Founder & Fundraising',
    image: 'https://static.prod-images.emergentagent.com/jobs/c1f6dc83-8b62-48dc-b396-cdfb9fb68a18/images/c1446eb465a4e0b2f96398f3178e505610f1f10668ed80c55b1e4127f24c41e3.jpeg',
    bio: "Avec un background en finance, Terence s'occupe de toute la gestion financière du fonds, des levées de fonds et des relations donateurs.",
    linkedin: 'https://www.linkedin.com/in/terence-dossou-5a3067160/',
  },
];

const milestones = [
  { year: '2019', title: 'Création', description: 'Fondation du fonds de dotation Second Souffle Production' },
  { year: '2020', title: 'Premières projections', description: 'Lancement des premières diffusions dans les écoles parisiennes' },
  { year: '2021', title: 'Expansion', description: 'Extension aux établissements pénitentiaires et partenariat avec Cinémaquis' },
  { year: '2022', title: 'Festival WiPP', description: 'Première édition du festival WiPP' },
  { year: '2023', title: 'International', description: 'Déploiement via les Centres Culturels Français à l\'étranger' },
  { year: '2024', title: 'Transformation', description: 'Évolution vers un modèle de distributeur non commercial' },
];

const AboutPage = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  
  const missionRef = useRef(null);
  const isMissionInView = useInView(missionRef, { once: true, margin: '-100px' });
  
  const teamRef = useRef(null);
  const isTeamInView = useInView(teamRef, { once: true, margin: '-100px' });
  
  const historyRef = useRef(null);
  const isHistoryInView = useInView(historyRef, { once: true, margin: '-100px' });

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-[#050505]" data-testid="about-hero">
          <div className="container-custom">
            <motion.div
              ref={heroRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <span className="overline mb-6 block">Qui sommes-nous</span>
              <h1 className="text-4xl md:text-6xl font-medium mb-6 leading-tight">
                Un fonds de dotation dédié au
                <span className="text-gradient"> cinéma indépendant</span>
              </h1>
              <p className="text-[#a1a1aa] text-lg md:text-xl leading-relaxed max-w-3xl">
                Second Souffle Production est un fonds de dotation dédié au cinéma indépendant. 
                Nous soutenons des œuvres engagées, ambitieuses et accessibles, qui mettent en lumière 
                des réalités humaines, sociales et culturelles. Par la distribution non commerciale, 
                nous contribuons à faire émerger les cinéastes de demain et à renforcer l'impact du cinéma 
                auprès du plus grand nombre.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-24 md:py-32 bg-[#0a0a0c]" data-testid="about-mission">
          <div className="container-custom">
            <motion.div
              ref={missionRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isMissionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="overline mb-4 block">Notre mission</span>
              <h2 className="text-3xl md:text-5xl font-medium">
                Distributeur <span className="text-gradient">non commercial</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Target,
                  title: 'Sélectionner',
                  description: 'Nous identifions des projets cinématographiques porteurs de sens : message environnemental, sociétal ou culturel fort.',
                },
                {
                  icon: Eye,
                  title: 'Acquérir',
                  description: 'Nous achetons les droits de diffusion non commerciaux auprès des producteurs grâce au soutien de nos mécènes.',
                },
                {
                  icon: Heart,
                  title: 'Diffuser',
                  description: 'Nous organisons des projections gratuites pour des publics qui n\'ont pas accès au cinéma traditionnel.',
                },
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isMissionInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="glass p-8 rounded-2xl text-center"
                  data-testid={`mission-item-${index}`}
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-6">
                    <item.icon size={28} />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-4">{item.title}</h3>
                  <p className="text-[#a1a1aa] leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isMissionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-16 glass p-8 md:p-12 rounded-2xl"
            >
              <p className="text-[#a1a1aa] text-lg leading-relaxed text-center max-w-4xl mx-auto">
                <span className="text-white font-medium">L'ensemble des fonds collectés</span> auprès 
                de nos mécènes sert exclusivement à financer ces achats de droits de diffusion et 
                l'organisation de ces actions. <span className="text-[#D4AF37]">Sans ces achats de droits, 
                aucune diffusion n'est possible.</span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-24 md:py-32 bg-[#050505]" data-testid="about-team">
          <div className="container-custom">
            <motion.div
              ref={teamRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="overline mb-4 block">Notre équipe</span>
              <h2 className="text-3xl md:text-5xl font-medium">
                Les visages de <span className="text-gradient">Second Souffle</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="glass rounded-2xl overflow-hidden card-hover"
                  data-testid={`about-team-member-${index}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-xl font-medium text-white">{member.name}</h3>
                      <a 
                        href={member.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-[#D4AF37] transition-colors"
                        aria-label={`LinkedIn de ${member.name}`}
                      >
                        <Linkedin size={18} />
                      </a>
                    </div>
                    <p className="text-[#D4AF37] text-sm mb-4">{member.role}</p>
                    <p className="text-[#a1a1aa] text-sm leading-relaxed">{member.bio}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection />
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
